import { errorDefinition, errorMessage, type ApiErrorBody } from "@planner/core";
import { Component, type ReactNode } from "react";
import { useLocale } from "../i18n/provider.js";
import { platform } from "../platform/index.js";

/**
 * **الشاشةُ التي انكسرت تقول إنها انكسرت** — لا بياضاً.
 *
 * فخطأٌ في الرسم يُسقط شجرة React كلَّها، **فيبقى المستخدم أمام صفحةٍ فارغة**:
 * لا رسالةَ ولا كودَ ولا زرَّ محاولةٍ ثانية. وهو أسوأُ فشلٍ صامتٍ في النظام،
 * لأنه الوحيد الذي لا يترك للمستخدم ما يقوله لنا.
 *
 * ويُكتب حارساً واحداً فوق الشجرة كلِّها، لا شاشةً شاشة: **حارسٌ يُركَّب يدوياً
 * على ثلاثٍ وأربعين شاشةً يُنسى على الرابعة والأربعين.**
 */

/**
 * **العطبُ الذي لا يمسكه أحد — له كودٌ ومرجعٌ يُقتبَس، كأيّ فشلٍ آخر.**
 *
 * فالمبدأ المركزيّ يقول: لا شيء يفشل، مهما صغر، بلا رسالةٍ مفهومة وكودٍ ثابت
 * يستطيع المستخدم أن يقوله. **وكان ذلك مبنيّاً في السيرفر وحده**: المعترضُ
 * يطبّع كلّ فشلٍ ويعطيه كوداً ورقمَ ربط.
 *
 * وفي المتصفّح لم يكن شيءٌ من ذلك: وعدٌ مرفوضٌ بلا `catch` يترك الشاشة **هيكلَ
 * تحميلٍ لا ينتهي**، وخطأٌ في الرسم يترك **بياضاً**. والمستخدم لا يرى كوداً ولا
 * مرجعاً — يرى شاشةً واقفة، فيعيد التحميل ويظنّ الغلط غلطَه.
 *
 * وأمسكه خطأٌ منّي: كتبتُ شكلَ ردٍّ لا يطابق ما يعيده السيرفر، فرُفض الوعد
 * داخل تأثيرٍ بلا `catch`، **وبقي الهيكلُ يدور ولا شيء يقول لماذا**.
 */

/** الكودُ الواحد لكلّ عطبٍ في المتصفّح — موجودٌ في الكتالوج، يقرؤه المستخدم فيه */
export const CRASH_CODE = "INTERNAL";

/**
 * يبني جسمَ الخطأ من أيّ سببٍ كان — **بمرجعٍ جديد يُقال ويُبحَث به**.
 *
 * والمرجعُ من هنا لا من السيرفر: العطبُ لم يصل إليه أصلاً. ويُكتب في سجلّ
 * المتصفّح مع السبب الكامل، **فمن قال المرجع وُجد به الأثر**.
 */
export function faultToError(cause: unknown): ApiErrorBody {
  const correlationId = platform.newId();
  platform.logFault(correlationId, cause);
  const code = staleBuild(cause) ? "APP_UPDATED" : CRASH_CODE;
  return { code, messageKey: errorDefinition(code).messageKey, correlationId };
}

/**
 * **ملفُّ شاشةٍ من نسخةٍ سابقة لم يعد موجوداً** — فالشاشاتُ تُحمَّل حين تُفتَح، والرفعُ
 * يحذف ملفاتِ النسخة القديمة. **ونصُّ المتصفّح يختلف بينها**، فتُقرأ الثلاثة.
 */
const STALE_CHUNK =
  /dynamically imported module|Importing a module script failed|Unable to preload CSS/i;

export function staleBuild(cause: unknown): boolean {
  return cause instanceof Error && STALE_CHUNK.test(cause.message);
}

type CrashState = { readonly error: ApiErrorBody | null };

export class CrashBoundary extends Component<{ readonly children: ReactNode }, CrashState> {
  override state: CrashState = { error: null };

  /** **والسببُ يُسجَّل تحت مرجعه** داخل `faultToError` — فلا سطرَ تسجيلٍ ثانٍ هنا */
  static getDerivedStateFromError(cause: unknown): CrashState {
    return { error: faultToError(cause) };
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (error === null) return this.props.children;
    return <CrashScreen error={error} />;
  }
}

/**
 * **والخروجُ الوحيد إعادةُ التحميل** — فحالةُ الشجرة بعد السقوط لا يُوثَق بها.
 */
function CrashScreen({ error }: { readonly error: ApiErrorBody }): ReactNode {
  const { t, locale } = useLocale();
  return (
    <div className="state" role="alert">
      <h2>{t("state.crashed.title")}</h2>
      <p>{errorMessage(locale, error)}</p>
      <div className="codes">
        <span className="code">{error.code}</span>
        <span className="code">{error.correlationId}</span>
      </div>
      <p className="field-hint">{t("state.crashed.quote")}</p>
      <button
        type="button"
        className="btn"
        onClick={() => {
          platform.reload();
        }}
      >
        {t("state.crashed.reload")}
      </button>
    </div>
  );
}
